import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import PropTypes from "prop-types";
import { useNotification } from "../../Context/notificationContext";
import { gql, useMutation } from "@apollo/client";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// ADD INGREDIENT MUTATION
const ADD_INGREDIENT = gql`
  mutation AddIngredient(
    $ingredient_name: String!
    $ingredient_type: IngredientType!
    $image_url: String
  ) {
    addIngredient(
      ingredient_name: $ingredient_name
      ingredient_type: $ingredient_type
      image_url: $image_url
    ) {
      id
      ingredient_name
      ingredient_type
      created_by
      created
      modified
      image_url
    }
  }
`;

const AddIngredients = ({
  newIngredientName,
  setNewIngredientName,
  ingredientType,
  setIngredientType,
  ingredientImage,
  setIngredientImage,
  ingredients,
  setIngredients,
  setAddIngredientActive,
  refetchIngredients,
}) => {
  const theme = useTheme();
  const { showNotification } = useNotification();

  const [
    addIngredient,
    { loading: addIngredientLoading, error: addIngredientError },
  ] = useMutation(ADD_INGREDIENT);

  // HANDLE ADD INGREDIENT
  const handleAddIngredient = async () => {
    try {
      if (!newIngredientName || !ingredientType) {
        return showNotification(
          "warning",
          "Please fill Ingredient Name & Type"
        );
      }
      const isIngredientAvailable = ingredients?.filter(
        (val) =>
          val?.ingredient_name?.toLowerCase() ===
          newIngredientName?.toLowerCase()
      );
      if (isIngredientAvailable.length > 0) {
        return showNotification(
          "warning",
          "Ingredient name already exists in the list."
        );
      }
      const response = await addIngredient({
        variables: {
          ingredient_name: newIngredientName,
          ingredient_type: ingredientType,
          image_url: ingredientImage,
        },
      });
      const newIngredient = response.data.addIngredient[0];
      setIngredients((prev) => [...prev, newIngredient]);
      refetchIngredients();
      setAddIngredientActive(false);
      return showNotification(
        "success",
        `"${newIngredientName}" has been added to the list!`
      );
    } catch (error) {
      console.log("addError", error);
      return showNotification("error", "Failed to add ingredient");
    }
  };
  if (addIngredientError) {
    showNotification("error", addIngredientError.message);
  }
  return (
    <form
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        margin: "20px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        boxShadow: theme.shadows[3],
      }}
    >
      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        sx={{ color: theme.palette.primary.main, width: "100%" }}
      >
        <Box
          onClick={() => setAddIngredientActive(false)}
          sx={{
            fontSize: "1.2rem",
            color: theme.palette.text.primary,
            margin: "0 auto 0 0",
            cursor: "pointer",
            transition: "150ms",
            marginTop: "-6px",
            padding: "8px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "fit-content",
            boxSizing: "border-box",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <ArrowBackIosNewIcon />
        </Box>
        <span style={{ textAlign: "center", display: "block" }}>
          Add Ingredient
        </span>
      </Typography>

      {/* New Ingredient Name Input */}
      <TextField
        label="Ingredient Name"
        type="text"
        variant="filled"
        margin="normal"
        name="item_name"
        value={newIngredientName}
        onChange={(e) => setNewIngredientName(e.target.value)}
        fullWidth
        sx={{
          backgroundColor: theme.palette.background.paper,
          marginBottom: "20px",
        }}
      />

      <FormControl variant="filled" fullWidth margin="normal">
        <InputLabel
          id="ingredient-type-label"
          sx={{ color: theme.palette.text.primary }}
        >
          Ingredient Type
        </InputLabel>
        <Select
          labelId="ingredient-type-label"
          value={ingredientType}
          onChange={(e) => setIngredientType(e.target.value)}
          sx={{
            backgroundColor: theme.palette.background.paper,
            marginBottom: "20px",
          }}
        >
          {[
            { id: 1, name: "Grain Item", value: "GRAIN" },
            { id: 2, name: "Spice Item", value: "SPICE" },
            { id: 3, name: "Oil Bottle Item", value: "OIL" },
            { id: 4, name: "Paste Bottle Item", value: "PASTE" },
            { id: 5, name: "Fresh Bowl/Cup Item", value: "FRESH" },
          ].map((ingredient) => (
            <MenuItem key={ingredient.id} value={ingredient.value}>
              {ingredient.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ marginTop: "20px" }}
          onClick={handleAddIngredient}
          disabled={addIngredientLoading}
        >
          {addIngredientLoading ? <CircularProgress size={24} /> : "Add"}
        </Button>
      </Stack>
    </form>
  );
};

AddIngredients.propTypes = {
  newIngredientName: PropTypes.string.isRequired,
  setNewIngredientName: PropTypes.func.isRequired,
  ingredientType: PropTypes.string.isRequired,
  setIngredientType: PropTypes.func.isRequired,
  ingredientImage: PropTypes.string,
  setIngredientImage: PropTypes.func,
  ingredients: PropTypes.array.isRequired,
  setIngredients: PropTypes.func.isRequired,
  setAddIngredientActive: PropTypes.func.isRequired,
  refetchIngredients: PropTypes.func.isRequired,
};

export default AddIngredients;
